import type {
  MyScheduleEverywhere,
  ScheduleAssignment,
  ScheduleBlockout,
} from "./my-schedule-queries";
import { DISPLAY_LOCALE } from "@how/ui";

/** "Sun 11 Oct, 9:00 AM CDT": the service's own time and zone, wherever the reader is. */
export function formatWhenAt(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleString(DISPLAY_LOCALE, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
    timeZoneName: "short",
  });
}

/** "A and B", "A, B and C". */
export function listChurchNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1] ?? ""}`;
}

/** The spec's link copy, exactly. */
export function openInLabel(churchName: string): string {
  return `Open in ${churchName}'s portal`;
}

/** Where "Open in <Church>'s portal" lands: My schedule, at that date (ADR 0070 R4). */
export function itemPath(assignmentId: string): string {
  return `/my-schedule?item=${assignmentId}`;
}

const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The date the address asks for (`?item=<id>`), or null. Anything that is not an id is ignored (it reaches a selector). */
export function targetItem(search: string): string | null {
  const value = new URLSearchParams(search).get("item");
  return value !== null && ID.test(value) ? value : null;
}

/**
 * Each live date that overlaps a live date at another church, with those churches' names (ADR 0066 R11). Declined
 * and cancelled dates are not conflicts; two dates at the same church are that church's business.
 */
export function findConflicts(
  assignments: readonly ScheduleAssignment[],
  churchNames: ReadonlyMap<string, string>,
): Map<string, string[]> {
  const live = assignments.filter((a) => a.response !== "declined" && !a.isCancelled);
  const out = new Map<string, string[]>();
  for (const a of live) {
    const start = Date.parse(a.startsAt);
    const end = Date.parse(a.endsAt);
    const others = new Set<string>();
    for (const b of live) {
      if (b.tenantId === a.tenantId) continue;
      if (Date.parse(b.startsAt) < end && Date.parse(b.endsAt) > start) {
        others.add(churchNames.get(b.tenantId) ?? "another church");
      }
    }
    if (others.size > 0)
      out.set(
        a.id,
        [...others].sort((x, y) => x.localeCompare(y)),
      );
  }
  return out;
}

export interface LabelledBlockout extends ScheduleBlockout {
  readonly churchName: string;
}

export interface PageSplit {
  readonly pending: readonly ScheduleAssignment[];
  /** Waiting dates at the church being viewed: the ones answered here, and Home's count (ruling S4). */
  readonly pendingHere: number;
  readonly accepted: readonly ScheduleAssignment[];
  readonly declined: readonly ScheduleAssignment[];
  /** The church being viewed is on the person's list: a record there is linked to the login (R14). */
  readonly linkedHere: boolean;
  /**
   * Every church's blockouts, each labelled with its church (ruling S1): a blockout set for "All my churches" stays
   * visible and removable from any of them.
   */
  readonly blockouts: readonly LabelledBlockout[];
  /** The scheduler's note on this church's first waiting date that has one. */
  readonly messageHere: string | null;
}

/** The page's three sections across every church; cancelled dates are not shown (R15). */
export function splitForPage(data: MyScheduleEverywhere, currentTenantId: string): PageSplit {
  const names = new Map(data.churches.map((c) => [c.tenantId, c.name]));
  const live = data.assignments.filter((a) => !a.isCancelled);
  const pending = live.filter((a) => a.response === "no_reply");
  const pendingHere = pending.filter((a) => a.tenantId === currentTenantId);
  return {
    pending,
    pendingHere: pendingHere.length,
    accepted: live.filter((a) => a.response === "accepted"),
    declined: live.filter((a) => a.response === "declined"),
    linkedHere: data.churches.some((c) => c.tenantId === currentTenantId),
    blockouts: data.blockouts.map((b) => ({ ...b, churchName: names.get(b.tenantId) ?? "" })),
    messageHere: pendingHere.find((a) => a.message !== null)?.message ?? null,
  };
}
